import { memo, useMemo } from "react";
import { Div, Icon, Image, lightenColor, Text, useTheme, type DivProps, type OmitProps } from "react-better-html";

import { useI18n } from "../../utils/i18n";

import DecorationCard from "./DecorationCard";

const membersArray = [1, 2, 3];

type DecorationMembersListCardProps = OmitProps<DivProps, "children"> & {
   members?: string[];
};

function DecorationMembersListCard({ members, ...props }: DecorationMembersListCardProps) {
   const theme = useTheme();
   const { t } = useI18n();

   const membersColora = useMemo<string[]>(
      () => [theme.colors.info, lightenColor(theme.colors.success, 0.2), theme.colors.warn],
      [theme],
   );

   const memberImageSize = members ? 30 : 24;
   const overlap = -(memberImageSize / 4);

   return (
      <DecorationCard whiteSpace="nowrap" width={members ? 200 : undefined} {...props}>
         {members && (
            <Text fontSize={14} fontWeight={700} marginBottom={theme.styles.gap}>
               {t((l) => l.components.decorations.membersList.title)}
            </Text>
         )}

         <Div.row alignItems="center" flexWrap="wrap" marginBottom={theme.styles.gap} marginLeft={-overlap}>
            {(members ?? membersArray).map((member, index) => (
               <Image.profileImage
                  size={memberImageSize}
                  letterColor={theme.colors.base}
                  border={`solid ${theme.styles.borderWidth}px ${theme.colors.backgroundContent}`}
                  backgroundColor={
                     members && index === 0 ? theme.colors.primary : membersColora[index % membersColora.length]
                  }
                  letters={
                     members
                        ? member.toString()
                        : t((l) => (l.components.decorations.membersList as any)[`member${member}`])
                  }
                  flexShrink={0}
                  marginLeft={overlap}
                  key={member}
               />
            ))}

            {members && (
               <Div.row
                  width={memberImageSize}
                  height={memberImageSize}
                  alignItems="center"
                  justifyContent="center"
                  backgroundColor={theme.colors.backgroundBase}
                  border={`dashed 2px ${theme.colors.backgroundThree}`}
                  borderRadius={999}
                  flexShrink={0}
                  marginLeft={overlap}
               >
                  <Icon name="plus" color={theme.colors.textSecondary} size={memberImageSize / 3} />
               </Div.row>
            )}
         </Div.row>

         <Text fontSize={12} color={theme.colors.textSecondary}>
            {members
               ? `${t((l) => l.components.decorations.membersList.description2)} ${members.length - 1} ${
                    members.length === 1 ? t((l) => l.common.member) : t((l) => l.common.members)
                 }`
               : t((l) => l.components.decorations.membersList.description)}
         </Text>
      </DecorationCard>
   );
}

export default memo(DecorationMembersListCard);
